import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { useCreditStore } from "store/creditStore";
import { FlightDetails, ConnectionFlight } from "../types";
import { FlightCardSkeleton } from "./SearchResultsSkeletons";
import FlightCard from "./FlightCard";
import ConnectionCard from "./ConnectionCard";
import StopsFilter from "./StopsFilter";
import { useFlightSelection } from "./useFlightSelection";
import { usePostRequest } from "./usePostRequest";
import RoutePager from "../RoutePager";
import AiSuggestiveFilters from "../AiSuggestiveFilters";
import FilterSortBar, { DurationSortOrder } from "../FilterSortBar";
import { filterFlightsByStops, filterConnectionsByStops } from "../routeSearchHelpers";
import { sortFlightDetails, sortConnectionFlights } from "./loadsUtils";
import { FlightSuggestion } from "apis/loadsServices";

interface SearchResultsProps {
  flights: FlightDetails[];
  connectionFlights?: ConnectionFlight[];
  stopsFilter: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
  onStopsFilterChange: (
    filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null,
  ) => void;
  sortBy?: string | null;
  sortOrder?: string | null;
  onSortChange?: (sortBy: string | null, sortOrder: string | null) => void;
  appliedSuggestionId?: string | null;
  appliedSuggestion?: FlightSuggestion | null;
  onDismissSuggestion?: () => void;
  suggestions?: FlightSuggestion[];
  isLoadingFlights?: boolean;
  // Set when the search request itself failed (e.g. a 500) — shown instead
  // of the "no flights found" empty state, which implies a valid empty result.
  searchError?: string | null;
  // The API's own explanation for a valid zero-result response (e.g. "Flight
  // 0N100 does not operate on COK-WAA") — shown in place of the generic
  // "No flights found ..." copy when the backend provides one.
  noResultsMessage?: string | null;
  show: boolean;
  searchDate: string;
  selectedDates?: string[];
  // Receives optimistic "recently posted" cards built from the current
  // selection so the home view can show them instantly after posting.
  onPostRequestSuccess?: (
    optimisticItems?: any[],
    options?: { keepResults?: boolean },
  ) => void;
  sourceLabel?: string;
  destinationLabel?: string;
  totalResults?: number;
  // Total item count straight from the API response's top-level `count`
  // field, used only for the "Select all N Results" label.
  apiResultCount?: number;
  // Raw direct/connection totals from the API (data.total / data.connectionTotal).
  // Used to distinguish "no direct flights, but connections exist" from "no
  // flights at all" instead of guessing from array lengths.
  directTotal?: number;
  connectionTotal?: number;
  onChangeDate?: () => void;
  onChangeRoute?: () => void;
  changeRouteLabel?: string;
  // Opens the Filter dropdown instead of directly changing the stops filter.
  onWidenSearch?: () => void;
  // Fetches every page of the current search (not just the one on screen) so
  // a date's "Select all" can pick up that same date's flights on other
  // pages too. Falls back to selecting only what's currently loaded when
  // omitted.
  onFetchAllForSelection?: () => Promise<{
    flights: FlightDetails[];
    connections: ConnectionFlight[];
  }>;
  hideConnectedFilter?: boolean;
  hideInternalFilters?: boolean;
  /** Shows a compact Sort By control on the results title row (Route search's Figma layout). */
  showSortInHeader?: boolean;
  readOnly?: boolean;
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  flights,
  connectionFlights = [],
  stopsFilter,
  onStopsFilterChange,
  sortBy,
  sortOrder,
  onSortChange,
  appliedSuggestionId = null,
  appliedSuggestion = null,
  onDismissSuggestion,
  suggestions = [],
  isLoadingFlights = false,
  searchError = null,
  noResultsMessage = null,
  show,
  searchDate,
  selectedDates = [],
  onPostRequestSuccess,
  sourceLabel,
  destinationLabel,
  totalResults = 0,
  apiResultCount,
  directTotal,
  connectionTotal,
  onChangeDate,
  onChangeRoute,
  changeRouteLabel = "Change route",
  onWidenSearch,
  onFetchAllForSelection,
  hideConnectedFilter = false,
  hideInternalFilters = false,
  showSortInHeader = false,
  readOnly = false,
  page = 1,
  totalPages = 1,
  onPageChange,
}) => {
  // Tokens deducted per posted request — fetched once by the Loads page.
  const requestCost = useCreditStore((state) => state.requestCost);

  // Selection model + toggles (direct flights and connection segments).
  const {
    selectedFlights,
    selectedSegments,
    selectedCount,
    toggleFlightSelection,
    isSegmentSelected,
    areAllSegmentsSelected,
    toggleSegmentSelection,
    toggleAllSegmentsInConnection,
    isAllSelected,
    addToSelection,
    removeFromSelectionWhere,
    clearSelection,
  } = useFlightSelection(
    stopsFilter,
    searchDate,
    selectedDates,
    sourceLabel,
    destinationLabel,
  );

  // Same date-key logic flightsByDate/connectionsByDate group by (below), so
  // a date's "Select all" can filter this page's OR every page's data down
  // to just that date.
  const flightDateKey = (flight: FlightDetails) =>
    flight.scheduleDate ||
    flight.date ||
    flight.departureTime?.split("T")[0] ||
    "unknown";
  const segmentDateKey = (segment: { departureTime?: string }) =>
    segment.departureTime?.split("T")[0] || "unknown";
  const connectionDateKey = (connection: ConnectionFlight) =>
    connection.overallDepartureTime?.split("T")[0] ||
    connection.segments[0]?.departureTime?.split("T")[0] ||
    "unknown";

  // While fetching every page to select/deselect one date's flights beyond
  // the current page — keyed by date so only that date's checkbox shows the
  // loading state.
  const [loadingDateKey, setLoadingDateKey] = useState<string | null>(null);

  const handleToggleDateGroupSelectAll = (
    pageFlights: FlightDetails[],
    pageConnections: ConnectionFlight[],
  ) => {
    const alreadyAllSelected = isAllSelected(pageFlights, pageConnections);

    if (alreadyAllSelected) {
      const flightKeysToRemove = new Set(
        pageFlights.map((f) => f.compositeId || String(f.id)),
      );
      const connectionIdsToRemove = new Set(
        pageConnections.map(
          (c) =>
            c.compositeId ||
            `${c.segments[0]?.id}_${c.overallDepartureTime}`,
        ),
      );

      removeFromSelectionWhere(
        (flight) =>
          flightKeysToRemove.has(flight.compositeId || String(flight.id)),
        (segment, connectionId) => connectionIdsToRemove.has(connectionId),
      );
    } else {
      addToSelection(
        filterFlightsByStops(pageFlights, stopsFilter),
        filterConnectionsByStops(pageConnections, stopsFilter),
      );
    }
  };

  // Post-request flow (validation, API, optimistic cards, credit refresh).
  const {
    posting,
    handlePostRequest,
    postingFlightKey,
    handlePostSingleFlight,
    isFlightPosted,
  } = usePostRequest({
    selectedFlights,
    selectedSegments,
    onPostRequestSuccess,
    clearSelection,
  });

  const d = new Date(searchDate);
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const month = d.toLocaleDateString(undefined, { month: "long" });
  const day = d.getDate();
  const year = d.getFullYear();
  const formattedDate = `${month} ${day} ${year} - ${weekday}`;

  // Format selected dates for display. A single date keeps the full
  // "Month Day Year - Weekday" form; multiple dates are compacted by
  // grouping consecutive same-month/year entries, e.g.
  // "September 11, 12, October 1, 2 - 2026".
  const formatDatesCompact = (dates: string[]): string => {
    const groups: { month: string; year: number; days: number[] }[] = [];
    dates.forEach((dateStr) => {
      const parsed = new Date(dateStr);
      if (isNaN(parsed.getTime())) return;
      const month = parsed.toLocaleDateString(undefined, { month: "long" });
      const year = parsed.getFullYear();
      const day = parsed.getDate();
      const last = groups[groups.length - 1];
      if (last && last.month === month && last.year === year) {
        last.days.push(day);
      } else {
        groups.push({ month, year, days: [day] });
      }
    });
    if (groups.length === 0) return "";
    const years = Array.from(new Set(groups.map((g) => g.year)));
    if (years.length === 1) {
      const monthParts = groups
        .map((g) => `${g.month} ${g.days.join(", ")}`)
        .join(", ");
      return `${monthParts} - ${years[0]}`;
    }
    return groups
      .map((g) => `${g.month} ${g.days.join(", ")} - ${g.year}`)
      .join(", ");
  };

  // Week/Month mode from the calendar sends a single "start - end" range
  // string in `selectedDates` instead of one entry per day — format that
  // separately so `new Date()` isn't handed an unparsable range string.
  const formatDateRangeString = (rangeStr: string): string | null => {
    const [startStr, endStr] = rangeStr.split(" - ");
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const format = (d: Date) =>
      `${d.toLocaleDateString(undefined, { month: "long" })} ${d.getDate()} ${d.getFullYear()}`;
    return `${format(start)} - ${format(end)}`;
  };

  const selectedDatesDisplay =
    selectedDates.length === 1 && selectedDates[0].includes(" - ")
      ? formatDateRangeString(selectedDates[0]) ?? formattedDate
      : selectedDates.length > 1
      ? formatDatesCompact(selectedDates)
      : selectedDates.length === 1
        ? (() => {
            const d = new Date(selectedDates[0]);
            const weekday = d.toLocaleDateString(undefined, {
              weekday: "long",
            });
            const month = d.toLocaleDateString(undefined, { month: "long" });
            const day = d.getDate();
            const year = d.getFullYear();
            return `${month} ${day} ${year} - ${weekday}`;
          })()
        : formattedDate;

  // Filter direct flights and connection flights based on active stops filter & sort options
  const visibleFlights = useMemo(
    () =>
      sortFlightDetails(
        filterFlightsByStops(flights, stopsFilter),
        sortBy,
        sortOrder,
      ),
    [flights, stopsFilter, sortBy, sortOrder],
  );

  const visibleConnections = useMemo(
    () =>
      sortConnectionFlights(
        filterConnectionsByStops(connectionFlights, stopsFilter),
        sortBy,
        sortOrder,
      ),
    [connectionFlights, stopsFilter, sortBy, sortOrder],
  );

  // Group visible flights and connection flights by date for rendering date headers
  const dateGroups = useMemo(() => {
    const dateMap = new Map<
      string,
      { flights: FlightDetails[]; connections: ConnectionFlight[] }
    >();

    visibleFlights.forEach((flight) => {
      const dateKey =
        flight.scheduleDate ||
        flight.date ||
        flight.departureTime?.split("T")[0] ||
        "unknown";
      if (!dateMap.has(dateKey))
        dateMap.set(dateKey, { flights: [], connections: [] });
      dateMap.get(dateKey)!.flights.push(flight);
    });

    visibleConnections.forEach((conn) => {
      const dateKey =
        conn.overallDepartureTime?.split("T")[0] ||
        conn.segments[0]?.departureTime?.split("T")[0] ||
        "unknown";
      if (!dateMap.has(dateKey))
        dateMap.set(dateKey, { flights: [], connections: [] });
      dateMap.get(dateKey)!.connections.push(conn);
    });

    const parseDateKey = (key: string): Date | null => {
      const iso = new Date(`${key}T00:00:00`);
      if (!isNaN(iso.getTime())) return iso;
      const loose = new Date(key);
      return isNaN(loose.getTime()) ? null : loose;
    };

    const sortedKeys = Array.from(dateMap.keys()).sort((a, b) => {
      const da = parseDateKey(a);
      const db = parseDateKey(b);
      if (da && db) return da.getTime() - db.getTime();
      if (da) return -1;
      if (db) return 1;
      return a.localeCompare(b);
    });

    const groups: {
      date: string;
      label: string;
      flights: FlightDetails[];
      connections: ConnectionFlight[];
    }[] = [];

    // Different date-key formats (flight.scheduleDate vs. connection's
    // split overallDepartureTime) can produce distinct map keys for the
    // same calendar day. Merge by formatted label so a day never renders
    // as two groups (duplicate date header / AI suggestion banner).
    const groupsByLabel = new Map<string, (typeof groups)[number]>();

    sortedKeys.forEach((dateKey) => {
      let label = dateKey;
      const d = parseDateKey(dateKey);
      if (d) {
        const month = d.toLocaleDateString("en-US", { month: "long" });
        const day = d.getDate();
        const year = d.getFullYear();
        const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
        label = `${month} ${day} ${year} - ${weekday}`;
      }
      const entry = dateMap.get(dateKey)!;

      const existing = groupsByLabel.get(label);
      if (existing) {
        existing.flights.push(...entry.flights);
        existing.connections.push(...entry.connections);
        return;
      }

      const group = {
        date: dateKey,
        label,
        flights: entry.flights,
        connections: entry.connections,
      };
      groupsByLabel.set(label, group);
      groups.push(group);
    });

    return groups;
  }, [visibleFlights, visibleConnections]);

  const displayedResultCount =
    Number(totalResults) || visibleFlights.length + visibleConnections.length;

  const routeTitle =
    sourceLabel && destinationLabel
      ? `Search Results:  ${sourceLabel} to ${destinationLabel}`
      : sourceLabel
        ? `Search Results:  ${sourceLabel}`
        : "Search Results";

  // Determine active AI suggestion for highlighting top card (ONLY when clicked)
  const activeSuggestion = useMemo(() => {
    if (appliedSuggestion) {
      return appliedSuggestion;
    }
    if (appliedSuggestionId && suggestions && suggestions.length > 0) {
      return suggestions.find((s) => s.id === appliedSuggestionId) || null;
    }
    return null;
  }, [appliedSuggestion, appliedSuggestionId, suggestions]);

  if (!show) return null;

  // Show "No Data Found" only when not loading and no results
  const showNoData =
    !isLoadingFlights &&
    visibleFlights.length === 0 &&
    visibleConnections.length === 0;

  // When the API reports totals, use them (rather than array lengths) to tell
  // "no direct flights, but connections exist" apart from "no flights at all"
  // for the default ("none" / non-stop) search state.
  const hasApiTotals =
    typeof directTotal === "number" && typeof connectionTotal === "number";
  const noDirectHasConnections =
    hasApiTotals &&
    stopsFilter === "none" &&
    directTotal === 0 &&
    connectionTotal! > 0 &&
    !isLoadingFlights;
  const noFlightsAtAll =
    hasApiTotals &&
    stopsFilter === "none" &&
    directTotal === 0 &&
    connectionTotal === 0;
  const routeOrFlightDescriptor =
    sourceLabel && destinationLabel
      ? `between ${sourceLabel} and ${destinationLabel}`
      : sourceLabel
        ? `for ${sourceLabel}`
        : "";

  let hasHighlightedTopCard = false;

  return (
    <section className="searchResult px-1">
      <div className="search-results">
        {/* Two-column layout: Filters sidebar + Results */}
        <div className={`${hideInternalFilters ? "" : "flex gap-6"}`}>
          {/* Left Sidebar — Stops filter */}
          <StopsFilter
            stopsFilter={stopsFilter}
            onStopsFilterChange={onStopsFilterChange}
            displayedResultCount={displayedResultCount}
            hideInternalFilters={hideInternalFilters}
            hideConnectedFilter={hideConnectedFilter}
          />

          {/* Right Content - Results */}
          <div className="flex-1 min-w-0">
            <div className="border-b border-gray-200 pb-3 sm:pb-4 mb-3 sm:mb-4">
              <div className="flex items-center gap-2.5 flex-wrap">
                <p className="text-grease-100 text-base sm:text-lg font-semibold">
                  {routeTitle}
                </p>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#EBF4FF] text-[#2B7FFF] text-xs font-medium">
                  <svg
                    className="w-3.5 h-3.5 text-[#2B7FFF] flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    {requestCost ?? "—"} token{requestCost === 1 ? "" : "s"} will be deducted per request.
                  </span>
                </span>
                {showSortInHeader && (
                  <div className="flex flex-col items-end ml-auto">
                    <span className="text-[#9CA3AF] text-xs font-normal mb-1">
                      Showing {displayedResultCount} {displayedResultCount === 1 ? "result" : "results"}
                    </span>
                    <FilterSortBar
                      hideFilter
                      compact
                      stopsFilter={stopsFilter}
                      onStopsFilterChange={onStopsFilterChange}
                      sortBy={sortBy}
                      sortOrder={sortOrder as DurationSortOrder | null}
                      onSortOrderChange={(order, newSortBy) =>
                        onSortChange?.(
                          newSortBy ?? (order ? "duration" : null),
                          order,
                        )
                      }
                      showSort={displayedResultCount > 0}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1.5 sm:mt-2 text-gray-500 text-xs sm:text-sm">
                <i className="st-calendar text-sm sm:text-base text-gray-400"></i>
                <span className="truncate text-gray-500 font-normal">{selectedDatesDisplay}</span>
              </div>
            </div>
            {noDirectHasConnections && (
              <div className="mb-4 p-3 sm:p-4 rounded-xl bg-[#FFFBEB] border border-[#FEF3C7] flex items-start gap-2.5">
                <i
                  className="st-info text-base text-[#F59E0B] mt-0.5"
                  aria-hidden="true"
                ></i>
                <div>
                  <p className="text-grease-100 text-sm font-semibold">
                    Connecting Flights Available
                  </p>
                  <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
                    No direct flights available {routeOrFlightDescriptor} on{" "}
                    {selectedDatesDisplay}. Connecting flights available:
                  </p>
                </div>
              </div>
            )}
            {isLoadingFlights ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                {[...Array(4)].map((_, index) => (
                  <FlightCardSkeleton key={index} />
                ))}
              </div>
            ) : searchError ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-3">
                  <svg
                    className="w-7 h-7 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M12 9v3.75m0 3.75h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-grease-100 text-base font-semibold">
                  Something went wrong
                </p>
                <p className="text-gray-400 text-sm mt-1">{searchError}</p>
              </div>
            ) : showNoData ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                  <svg
                    className="w-7 h-7 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {stopsFilter === "none" && noFlightsAtAll ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      {noResultsMessage ||
                        `No flights found ${routeOrFlightDescriptor} on ${selectedDatesDisplay}.`}
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      Try adjusting your search to see more options.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {onChangeDate && (
                        <button
                          onClick={onChangeDate}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-grease-100 text-sm font-medium rounded-lg transition-colors"
                        >
                          Change date
                        </button>
                      )}
                      {onChangeRoute && (
                        <button
                          onClick={onChangeRoute}
                          className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-grease-100 text-sm font-medium rounded-lg transition-colors"
                        >
                          {changeRouteLabel}
                        </button>
                      )}
                      {!noResultsMessage && (
                        <button
                          onClick={onWidenSearch}
                          className="px-4 py-2 bg-[#FFBC00] hover:bg-[#e6a800] text-[#1F2937] text-sm font-medium rounded-lg transition-colors"
                        >
                          Widen search
                        </button>
                      )}
                    </div>
                  </>
                ) : stopsFilter === "none" ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No direct flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      Try searching with a stopover to see more options.
                    </p>
                    <button
                      onClick={onWidenSearch}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-[#1F2937] text-sm font-medium rounded-lg transition-colors"
                    >
                      Modify your filter
                    </button>
                  </>
                ) : stopsFilter === "one-or-fewer" ? (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      We couldn't find any flights matching your filters.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("none")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-[#1F2937] text-sm font-medium rounded-lg transition-colors"
                    >
                      Reset to non-stop
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-grease-100 text-base font-semibold">
                      No flights found
                    </p>
                    <p className="text-gray-400 text-sm mt-1 mb-4">
                      We couldn't find any flights matching your filters.
                    </p>
                    <button
                      onClick={() => onStopsFilterChange("none")}
                      className="px-5 py-2.5 bg-[#FFBC00] hover:bg-[#e6a800] text-[#1F2937] text-sm font-medium rounded-lg transition-colors"
                    >
                      Reset to non-stop
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {dateGroups.map((group) => {
                  const resultCountToDisplay =
                    group.flights.length + group.connections.length;

                  const allSelected = isAllSelected(
                    group.flights,
                    group.connections,
                  );

                  return (
                    <div key={group.date} className="mb-6">
                      {/* Date and Day Heading */}
                      <div className="flex items-center gap-2 mb-2">
                        <i className="st-calendar text-gray-400 text-sm sm:text-base"></i>
                        <span className="text-grease-100 text-sm sm:text-base font-semibold">
                          {group.label}
                        </span>
                      </div>

                      {/* Select All and count BELOW the date */}
                      {!readOnly ? (
                        <label
                          className={`flex items-center gap-2 mb-4 select-none ${
                            resultCountToDisplay === 0
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={allSelected}
                            disabled={resultCountToDisplay === 0}
                            onChange={() =>
                              handleToggleDateGroupSelectAll(
                                group.flights,
                                group.connections,
                              )
                            }
                            className="w-4 h-4 rounded-sm border border-gray-400 text-[#FFBC00] focus:ring-0 cursor-pointer disabled:cursor-not-allowed"
                          />
                          <span className="text-gray-700 text-xs sm:text-sm font-medium">
                            {loadingDateKey === group.date
                              ? "Selecting…"
                              : resultCountToDisplay === 0
                                ? "Select all 0 Results"
                                : resultCountToDisplay === 1
                                  ? "Select this Result"
                                  : `Select all ${resultCountToDisplay} Results`}
                          </span>
                        </label>
                      ) : null}

                      {/* AI Suggestion Heading & Vertical Connector Line above top card */}
                      {activeSuggestion && !hasHighlightedTopCard && (
                        <div className="flex flex-col items-start pl-1 mb-1">
                          <div className="flex items-center gap-1.5 text-[#1F2937] font-bold text-sm sm:text-base tracking-tight">
                            <svg
                              className="w-4 h-4 text-[#FFBC00] fill-[#FFBC00] shrink-0"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                            </svg>
                            <span>{activeSuggestion.label}</span>
                            {onDismissSuggestion && (
                              <button
                                type="button"
                                onClick={onDismissSuggestion}
                                className="p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                                title="Clear AI suggestion"
                                aria-label="Clear AI suggestion"
                              >
                                <X className="w-3.5 h-3.5 text-gray-500" />
                              </button>
                            )}
                          </div>
                          <div className="w-[2px] h-3.5 bg-[#FFBC00] ml-[7px] -mb-[2px] relative z-10" />
                        </div>
                      )}

                      {/* Direct Flights Grid */}
                      {group.flights.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-4">
                          {group.flights.map((flight) => {
                            const flightCompositeId =
                              flight.compositeId || String(flight.id);
                            const isSelected = selectedFlights.some(
                              (f) =>
                                (f.compositeId || String(f.id)) ===
                                flightCompositeId,
                            );

                            const shouldHighlight =
                              !hasHighlightedTopCard && activeSuggestion != null;
                            if (shouldHighlight) {
                              hasHighlightedTopCard = true;
                            }

                            return (
                              <FlightCard
                                key={flightCompositeId}
                                flight={flight}
                                isSelected={isSelected}
                                readOnly={readOnly}
                                onSelect={toggleFlightSelection}
                                onPostRequest={handlePostSingleFlight}
                                posting={
                                  postingFlightKey === flightCompositeId
                                }
                                posted={isFlightPosted(flightCompositeId)}
                                isHighlighted={shouldHighlight}
                              />
                            );
                          })}
                        </div>
                      )}

                      {/* Connection Flights Grid */}
                      {group.connections.length > 0 && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                            {group.connections.map((connection, index) => {
                              const shouldHighlightConn =
                                !hasHighlightedTopCard &&
                                activeSuggestion != null &&
                                group.flights.length === 0;
                              if (shouldHighlightConn) {
                                hasHighlightedTopCard = true;
                              }

                              return (
                                <ConnectionCard
                                  key={
                                    connection.compositeId ||
                                    `connection-${connection.segments[0]?.id}-${index}`
                                  }
                                  connection={connection}
                                  index={index}
                                  readOnly={readOnly}
                                  isSegmentSelected={isSegmentSelected}
                                  areAllSegmentsSelected={areAllSegmentsSelected}
                                  toggleSegmentSelection={toggleSegmentSelection}
                                  toggleAllSegmentsInConnection={
                                    toggleAllSegmentsInConnection
                                  }
                                  isHighlighted={shouldHighlightConn}
                                />
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <RoutePager
              page={page}
              totalPages={totalPages}
              onPageChange={(p) => onPageChange?.(p)}
              rightContent={
                !readOnly &&
                (selectedFlights.length > 0 || selectedSegments.length > 0) ? (
                  <button
                    type="button"
                    disabled={posting}
                    className={`px-6 py-3 w-60 rounded-md text-[#1F2937] text-center text-sm font-semibold bg-[#FFBC00] shadow-button transition-all ${
                      posting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handlePostRequest();
                    }}
                  >
                    {posting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        Posting...
                      </span>
                    ) : (
                      <>
                        Post{" "}
                        {selectedFlights.length + selectedSegments.length}{" "}
                        Request
                        {selectedFlights.length + selectedSegments.length !== 1
                          ? "s"
                          : ""}
                      </>
                    )}
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchResults;
